<script lang="ts">
    import type {DocumentDetailReactivePageData, DocumentDetailStaticPageData} from '../types';
    import type {DocumentAvailability} from 'typings/portaro.be.types';
    import {exists} from 'shared/utils/custom-utils';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpDocumentDetailPageService} from '../kp-document-detail-page.service';
    import {getPageContext} from 'shared/layouts/page-context';
    import {onDestroy, onMount} from 'svelte';
    import KpCover from '../../kp-cover/KpCover.svelte';
    import KpRating from '../../kp-rating/KpRating.svelte';
    import KpCustomParagraph
        from 'shared/value-editors/internal/editors/search-or-edit/templates/KpCustomParagraph.svelte';
    import KpAvailabilityButton from '../../../loan/kp-availability-button/KpAvailabilityButton.svelte';
    import KpAvailabilityInfo from '../../kp-availability-info/KpAvailabilityInfo.svelte';
    import KpFindInDatabases from '../../kp-find-in-databases/KpFindInDatabases.svelte';
    import KpHorizontalSeparator from 'shared/ui-widgets/separator/KpHorizontalSeparator.svelte';

    const localize = getLocalization();
    const service = getInjector().getByClass(KpDocumentDetailPageService);
    const pageContext = getPageContext<DocumentDetailStaticPageData, DocumentDetailReactivePageData>();

    const model = pageContext.staticData.model;
    let availability: DocumentAvailability | undefined;
    const reactiveDataUnsubscribe = pageContext.reactiveData.subscribe((data) => availability = data.availability);

    let loadingDocumentDownloadUrl = true;
    let documentDownloadUrl: string | null;

    onMount(async () => {
        documentDownloadUrl = await service.getDocumentDownloadUrl(model.record);
        loadingDocumentDownloadUrl = false;
    });

    onDestroy(() => {
        reactiveDataUnsubscribe();
    });

    const handleAvailabilityButtonClick = async () => {
        if (exists(documentDownloadUrl)) {
            return;
        }

        await service.requestDocument(model.record);
    }
</script>

<div class="record-details detail-blok-main">
    <!-- Returned today -->
    {#if exists(availability) && availability.returnedToday && (availability.pickupable || availability.orderable)}
        <img class="document-returned-today-image"
             src="/resources/img/icons/returned_today_80.png"
             width="80"
             height="80"
             alt="Dnes vráceno"
             title="Dnes vráceno"/>
    {/if}

    <!-- Cover -->
    <div class="cover-container-document-detail">
        <KpCover record={model.record}
                 target="cover"
                 fillTo="width"
                 canManageCover={model.filesManageable}
                 mainRecordDetailCover/>
    </div>

    <div class="details-container">
        <KpCustomParagraph content={model.nextToCoverParagraph} customClass="paragraph-next-to-cover"/>

        <!-- Rating -->
        {#if model.rateable}
            <KpRating record={model.record}/>
        {/if}

        <!-- Availability of document -->
        {#if exists(availability) && !loadingDocumentDownloadUrl}
            <div class="document-availability-container">
                <KpAvailabilityButton {availability}
                                      forExemplar={false}
                                      block={false}
                                      downloadUrl={documentDownloadUrl}
                                      on:click={handleAvailabilityButtonClick}/>

                <div class="availability-info">
                    <KpAvailabilityInfo {availability}/>
                </div>
            </div>
        {/if}

        <!-- Foreign occurrences -->
        {#if model.record.withForeignOccurrences}
            <div class="foreign-occurrences-container">
                <KpFindInDatabases recordId={model.record.id}/>
            </div>
        {/if}

        <!-- File previews -->
        {#if model.filePreviewItems.length > 0}
            <div class="preview-container">
                <span>{localize(/* @kp-localization commons.Nahled */ 'commons.Nahled')}: </span>

                {#each model.filePreviewItems as filePreviewItem, index}
                    <a href={filePreviewItem.href} target="_blank">
                        {filePreviewItem.text}
                    </a>

                    {#if index < model.filePreviewItems.length - 1}
                        <span>, </span>
                    {/if}
                {/each}
            </div>
        {/if}
    </div>

    <!-- Clear floating values -->
    <div class="clear-floating"></div>
    <br/>

    <!-- Under-cover paragraph -->
    <KpCustomParagraph content={model.underCoverParagraph} customClass="paragraph-under-cover"/>

    <!-- Line separator -->
    <KpHorizontalSeparator marginOverride="24px 0 0 0"/>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .record-details {
        .document-returned-today-image {
            position: absolute;
            margin-left: -50px;
            z-index: 30;
        }

        .cover-container-document-detail {
            float: left;
            width: 204px;
            margin-right: 20px;
            margin-bottom: @spacing-sm;
        }

        .document-availability-container,
        .preview-container {
            margin: 12px 0;
        }

        .availability-info {
            margin-top: @spacing-s;
        }

        .clear-floating {
            clear: left;
        }
    }

    :global {
        .document-availability-container .availability-button {
            display: inline-block;
        }
    }
</style>